import React, { Suspense, useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { LayoutGroup, MotionConfig } from "framer-motion";
import "./Font/font.css";
import "./print/print.css";
import "./site/site.css";
import "./site/phone.css";
import "./site/printer.css";
import "./site/story.css";
import JsonData from "./data/data.json";
import Navbar from "./site/Navbar";
import Footer from "./site/Footer";
import HomePage from "./site/HomePage";
import Seo from "./site/Seo";
import { cityList } from "./site/siteInfo";
import { mobileApp } from "./site/schema";
import { trackPageView } from "./site/analytics";
import { FileProvider } from "./Context";
import { PrintFlowProvider } from "./print/state/PrintFlowContext";
import { ToastProvider, Spinner } from "./print/components/ui";

// Heavier screens are code-split so the landing page stays light.
const GuidePage = React.lazy(() => import("./site/pages/GuidePage"));
const PrivacyPage = React.lazy(() => import("./site/pages/PrivacyPage"));
const TermsPage = React.lazy(() => import("./site/pages/TermsPage"));
const RefundPage = React.lazy(() => import("./site/pages/RefundPage"));
const ContactPage = React.lazy(() => import("./site/pages/ContactPage"));
const FaqPage = React.lazy(() => import("./site/pages/FaqPage"));
const NotFoundPage = React.lazy(() => import("./site/pages/NotFoundPage"));
const ShopsIndexPage = React.lazy(() => import("./site/pages/ShopsPage").then((m) => ({ default: m.ShopsIndexPage })));
const CityPage = React.lazy(() => import("./site/pages/ShopsPage").then((m) => ({ default: m.CityPage })));
const ShopPage = React.lazy(() => import("./site/pages/ShopsPage").then((m) => ({ default: m.ShopPage })));
const DeleteAccount = React.lazy(() => import("./components/deleteAcount"));
const FileViewer = React.lazy(() => import("./PrintComponents/FileReview"));
const PrintPage = React.lazy(() => import("./print/pages/PrintPage"));
const ShopEntryPage = React.lazy(() => import("./print/pages/ShopEntryPage"));
const LocationPage = React.lazy(() => import("./print/pages/LocationPage"));
const LoginPage = React.lazy(() => import("./print/pages/LoginPage"));
const CheckoutPage = React.lazy(() => import("./print/pages/CheckoutPage"));
const PaymentStatusPage = React.lazy(() => import("./print/pages/PaymentStatusPage"));
const TrackOrderPage = React.lazy(() => import("./print/pages/TrackOrderPage"));
const OrdersPage = React.lazy(() => import("./print/pages/OrdersPage"));
const AccountPage = React.lazy(() => import("./print/pages/AccountPage"));
const GetAppPage = React.lazy(() => import("./print/pages/GetAppPage"));
const ReferPage = React.lazy(() => import("./print/pages/ReferPage"));
const OffersPage = React.lazy(() => import("./print/pages/OffersPage"));

const isProduction = process.env.NODE_ENV === "production";

// Production-only deterrent against opening the developer tools (kept from the original site).
if (isProduction && typeof window !== "undefined") {
  const detectDevToolsOpen = () => {
    const threshold = 160;
    return window.outerHeight - window.innerHeight > threshold || window.outerWidth - window.innerWidth > threshold;
  };
  const redirectIfOpen = () => {
    if (detectDevToolsOpen()) window.location.href = "https://www.google.com";
  };
  window.addEventListener("resize", redirectIfOpen);
  redirectIfOpen();
  document.addEventListener("keydown", (e) => {
    if (e.key === "F12" || (e.ctrlKey && e.shiftKey && e.key === "I") || (e.ctrlKey && e.shiftKey && e.key === "C")) {
      e.preventDefault();
    }
  });
  document.addEventListener("contextmenu", (e) => e.preventDefault());
}

const PageFallback = () => (
  <div className="pa pa-page pa-page--center">
    <Spinner size="lg" label="Loading…" />
  </div>
);

/** Publishes the fixed navbar's height as --pa-nav-h so pages can sit below it. */
function useNavbarHeight() {
  useEffect(() => {
    const menu = document.getElementById("menu");
    if (!menu) return undefined;
    const apply = () => document.documentElement.style.setProperty("--pa-nav-h", `${Math.round(menu.getBoundingClientRect().height)}px`);
    apply();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(apply) : null;
    if (ro) ro.observe(menu);
    window.addEventListener("resize", apply);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", apply);
    };
  }, []);
}

/** Scrolls to the top on route change, or to the hash target on the home page. */
function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.slice(1);
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
      return () => clearTimeout(t);
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" in window ? "instant" : "auto" });
    return undefined;
  }, [pathname, hash]);
  return null;
}

/* Head tags for the pages of the print flow and the account area (the content pages carry their own <Seo>). */
const SEO = {
  "/print": {
    title: "Print PDF or photos online, collect at a shop near you",
    description: `Upload a PDF or photo, choose copies, colour and sides, pay via UPI and collect the printout at a PrintAnywhere partner shop in ${cityList("or")}.`,
    path: "/print",
  },
  "/print/shop": { title: "Print at this shop", path: "/print", noindex: true },
  "/print/location": { title: "Choose a print shop", path: "/print/location", noindex: true },
  "/print/checkout": { title: "Review and pay", path: "/print/checkout", noindex: true },
  "/print/payment-status": { title: "Payment status", path: "/print/payment-status", noindex: true },
  "/login": { title: "Log in", path: "/login", noindex: true },
  "/orders": { title: "My orders", path: "/orders", noindex: true },
  "/track": { title: "Track your order", path: "/orders", noindex: true },
  "/account": { title: "My account", path: "/account", noindex: true },
  "/get-app": {
    title: "PrintAnywhere app for Android and iPhone",
    description: "Download the PrintAnywhere app: print PDFs and photos from your phone, pay via UPI, track the job live and collect at a partner shop.",
    path: "/get-app",
    jsonLd: mobileApp(),
  },
  "/refer": {
    title: "Refer and earn: give ₹5, get ₹5",
    description: "Share your PrintAnywhere referral code. When a friend completes their first print you both earn ₹5 in rewards to spend on printing.",
    path: "/refer",
  },
  "/offers": {
    title: "Offers, coupons and rewards",
    description: "Referral bonuses, reward points that pay up to 75% of a bill, multi-copy pricing and the coupons on your PrintAnywhere account.",
    path: "/offers",
  },
  "/delete-account": { title: "Delete your account", description: "Request deletion of your PrintAnywhere account and the data linked to it.", path: "/delete-account" },
  "/file-viewer": { title: "File viewer", path: "/file-viewer", noindex: true },
};

function Page({ seo, children }) {
  return (
    <>
      <Seo {...seo} />
      {children}
    </>
  );
}

/** Reports a page view to analytics on every route change. */
function RouteAnalytics() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    const t = setTimeout(() => trackPageView(pathname + search), 300);
    return () => clearTimeout(t);
  }, [pathname, search]);
  return null;
}

const App = () => {
  useNavbarHeight();
  const { pathname } = useLocation();
  const hideFooter = pathname === "/print";

  return (
    <MotionConfig reducedMotion="user">
    <LayoutGroup>
    <ToastProvider>
      <PrintFlowProvider>
        <FileProvider>
          <ScrollManager />
          <RouteAnalytics />
          <Navbar />
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/user-guide" element={<GuidePage />} />
              <Route path="/privacy-policy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/refund-policy" element={<RefundPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/print-shops" element={<ShopsIndexPage />} />
              <Route path="/print-shops/:city" element={<CityPage />} />
              <Route path="/print-shops/:city/:slug" element={<ShopPage />} />
              <Route path="/delete-account" element={<Page seo={SEO["/delete-account"]}><DeleteAccount /></Page>} />
              <Route path="/file-viewer" element={<Page seo={SEO["/file-viewer"]}><FileViewer data={JsonData.printIcons} /></Page>} />

              <Route path="/print" element={<Page seo={SEO["/print"]}><PrintPage /></Page>} />
              <Route path="/print/shop/:shopId" element={<Page seo={SEO["/print/shop"]}><ShopEntryPage /></Page>} />
              <Route path="/print/location" element={<Page seo={SEO["/print/location"]}><LocationPage /></Page>} />
              <Route path="/print/checkout" element={<Page seo={SEO["/print/checkout"]}><CheckoutPage /></Page>} />
              <Route path="/print/payment-status" element={<Page seo={SEO["/print/payment-status"]}><PaymentStatusPage /></Page>} />
              <Route path="/login" element={<Page seo={SEO["/login"]}><LoginPage /></Page>} />
              <Route path="/orders" element={<Page seo={SEO["/orders"]}><OrdersPage /></Page>} />
              <Route path="/orders/:printerLocationId/:orderNo" element={<Page seo={SEO["/track"]}><TrackOrderPage /></Page>} />
              <Route path="/account" element={<Page seo={SEO["/account"]}><AccountPage /></Page>} />
              <Route path="/get-app" element={<Page seo={SEO["/get-app"]}><GetAppPage /></Page>} />
              <Route path="/refer" element={<Page seo={SEO["/refer"]}><ReferPage /></Page>} />
              <Route path="/offers" element={<Page seo={SEO["/offers"]}><OffersPage /></Page>} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
          {hideFooter ? null : <Footer />}
        </FileProvider>
      </PrintFlowProvider>
    </ToastProvider>
    </LayoutGroup>
    </MotionConfig>
  );
};

export default App;
